import { CommonModule } from '@angular/common';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
} from '@ngx-formly/core';
import {
  BgeCompletenessComparisonColumn,
  BgeCompletenessComparisonItemList,
  BgeCompletenessComparisonRecordGroup,
} from '@intellio/shared/models';
// The mock uses the same inline viewer bridge as the application shell.
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AppFileViewerService } from '@intellio/shared/services';
import { ApplicationFacade } from '@intellio/shared/state-mgmt';
import { Subject, take, takeUntil } from 'rxjs';
import { BgeCompletenessPassFailComponent } from './bge-completeness-pass-fail.component';

interface BgeCompletenessComparisonProps extends FormlyFieldProps {
  comparisonName: string;
  sourcePath: string;
  columns?: BgeCompletenessComparisonColumn[];
  coordinateColumns?: BgeCompletenessComparisonColumn[];
  statusKey: string;
  messageKey: string;
  recordGroups?: BgeCompletenessComparisonRecordGroup[];
  itemList?: BgeCompletenessComparisonItemList;
  previewPath?: string;
  headingVariant?: 'default' | 'section';
  statusMessageDisplay?: 'inline' | 'tooltip';
  columnsPerRow?: 1 | 2 | 3 | 4;
}

type JsonRecord = Record<string, unknown>;

export interface BgeCompletenessComparisonConfig
  extends FormlyFieldConfig<BgeCompletenessComparisonProps> {
  type: 'bge-completeness-comparison';
}

@Component({
  selector: 'itc-bge-completeness-comparison',
  template: `
    <div
      class="bge-completeness-comparison__heading"
      [class.bge-completeness-comparison__heading--section]="
        props.headingVariant === 'section'
      "
    >
      <span class="bge-completeness-comparison__title">{{
        props.comparisonName
      }}</span>
      <itc-bge-completeness-pass-fail
        [passed]="statusPassed"
        [message]="statusMessage"
        [messageDisplay]="props.statusMessageDisplay ?? 'inline'"
      ></itc-bge-completeness-pass-fail>
    </div>
    <div class="bge-completeness-comparison__container">
      <div
        class="bge-completeness-comparison__values"
        [ngClass]="columnsPerRowClass"
      >
        @for (column of visibleColumns; track column.key) {
        <mat-form-field
          [class.bge-completeness-comparison__value--full-width]="
            column.fullWidth
          "
        >
          <mat-label>{{ column.label }}</mat-label>
          @if (column.display === 'textarea') {
          <textarea
            matInput
            readonly
            cdkTextareaAutosize
            [cdkAutosizeMinRows]="column.autosizeMinRows ?? 3"
            [cdkAutosizeMaxRows]="column.autosizeMaxRows ?? 8"
            [value]="displayValue(readValue(column.key), column)"
            [attr.aria-label]="column.label"
          ></textarea>
          } @else {
          <input
            matInput
            readonly
            [value]="displayValue(readValue(column.key), column)"
            [attr.aria-label]="column.label"
          />
          }
        </mat-form-field>
        } @if (previewFileName) {
        <button
          mat-stroked-button
          type="button"
          class="bge-completeness-comparison__preview"
          [disabled]="isPreviewLoading"
          (click)="togglePreview()"
        >
          <mat-icon>{{ isPreviewOpen ? 'close' : 'visibility' }}</mat-icon>
          {{
            isPreviewOpen
              ? 'Close preview'
              : isPreviewLoading
              ? 'Loading preview'
              : 'Preview file'
          }}
        </button>
        }
      </div>
      @if (visibleRecordGroups.length) {
      <div class="bge-completeness-comparison__records">
        @for (group of visibleRecordGroups; track group.key) { @let records =
        recordsFor(group);
        <section
          class="bge-completeness-comparison__record-group"
          [attr.aria-label]="group.label"
        >
          <div class="bge-completeness-comparison__record-header">
            <span class="bge-completeness-comparison__record-label">
              {{ group.label }}
            </span>
            <span
              class="bge-completeness-comparison__record-count"
              [attr.aria-label]="records.length + ' matches'"
            >
              {{ records.length }}
            </span>
          </div>

          <div class="bge-completeness-comparison__record-table-container">
            <table
              mat-table
              class="bge-completeness-comparison__record-table"
              [dataSource]="pagedRecordsFor(group)"
            >
              <ng-container matColumnDef="record-title">
                <th mat-header-cell *matHeaderCellDef>
                  {{ recordTitleLabel(group) }}
                </th>
                <td
                  mat-cell
                  *matCellDef="let record"
                  [attr.data-label]="recordTitleLabel(group)"
                >
                  @if (recordLink(group, record); as link) {
                  <a [href]="link">{{ recordTitle(group, record) }}</a>
                  } @else {
                  <span>{{ recordTitle(group, record) }}</span>
                  }
                </td>
              </ng-container>

              @for (column of group.columns ?? []; track column.key; let
              columnIndex = $index) {
              <ng-container [matColumnDef]="recordColumnId(columnIndex)">
                <th mat-header-cell *matHeaderCellDef>{{ column.label }}</th>
                <td
                  mat-cell
                  *matCellDef="let record"
                  [attr.data-label]="column.label"
                >
                  {{ displayValue(valueFrom(record, column.key), column) }}
                </td>
              </ng-container>
              }

              <tr mat-header-row *matHeaderRowDef="recordColumnIds(group)"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: recordColumnIds(group)"
              ></tr>
            </table>
          </div>

          @if (records.length > pageSizeFor(group)) {
          <mat-paginator
            [length]="records.length"
            [pageIndex]="pageIndexFor(group)"
            [pageSize]="pageSizeFor(group)"
            [hidePageSize]="true"
            [showFirstLastButtons]="true"
            [attr.aria-label]="'Pagination for ' + group.label"
            (page)="onRecordPage(group.key, $event)"
          ></mat-paginator>
          }
        </section>
        }
      </div>
      } @if (itemList; as list) {
      <div class="bge-completeness-comparison__items">
        @for (item of itemsFor(list); track $index) {
        <div class="bge-completeness-comparison__item">
          <div class="bge-completeness-comparison__heading">
            <span class="bge-completeness-comparison__title">{{
              itemTitle(item, list)
            }}</span>
            <itc-bge-completeness-pass-fail
              [passed]="itemStatus(item, list)"
              [message]="itemDisplayMessage(item, list)"
              [accessibleMessage]="itemMessage(item, list)"
            ></itc-bge-completeness-pass-fail>
          </div>
          <div class="bge-completeness-comparison__values">
            @for (column of list.columns ?? []; track column.key) {
            <mat-form-field>
              <mat-label>{{ column.label }}</mat-label>
              <input
                matInput
                readonly
                [value]="displayValue(valueFrom(item, column.key), column)"
                [attr.aria-label]="column.label"
              />
            </mat-form-field>
            } @if (itemPreviewFileName(item, list); as fileName) {
            <button
              mat-stroked-button
              type="button"
              class="bge-completeness-comparison__preview"
              [disabled]="isPreviewLoading"
              (click)="togglePreview(fileName)"
            >
              <mat-icon>{{
                isPreviewOpenFor(fileName) ? 'close' : 'visibility'
              }}</mat-icon>
              {{
                isPreviewOpenFor(fileName)
                  ? 'Close preview'
                  : isPreviewLoading
                  ? 'Loading preview'
                  : 'Preview file'
              }}
            </button>
            }
          </div>
        </div>
        }
      </div>
      }
    </div>
  `,
  styleUrls: ['./bge-completeness-comparison.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatTableModule,
    CdkTextareaAutosize,
    BgeCompletenessPassFailComponent,
  ],
})
export class BgeCompletenessComparisonComponent
  extends FieldType<FieldTypeConfig<BgeCompletenessComparisonProps>>
  implements OnInit, OnDestroy
{
  private readonly destroyed$ = new Subject<void>();
  private readonly recordPageIndexes = new Map<string, number>();
  private applicationId?: string;
  private previewObjectUrl?: string;
  private previewedFileName?: string;
  isPreviewLoading = false;

  constructor(
    private readonly appFileViewerService: AppFileViewerService,
    private readonly appFacade: ApplicationFacade,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.appFacade.currentApplication$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((application) => {
        this.applicationId = application?.id;
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
    }

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get source(): JsonRecord {
    const source = this.resolvePath(this.props.sourcePath, this.rootModel);

    return this.asRecord(source);
  }

  get columns(): BgeCompletenessComparisonColumn[] {
    return this.props.columns ?? [];
  }

  get visibleColumns(): BgeCompletenessComparisonColumn[] {
    return this.showCoordinates
      ? [...this.columns, ...(this.props.coordinateColumns ?? [])]
      : this.columns;
  }

  get columnsPerRowClass(): string {
    const columnsPerRow = this.props.columnsPerRow;
    return columnsPerRow
      ? `bge-completeness-comparison__values--columns-${columnsPerRow}`
      : '';
  }

  get recordGroups(): BgeCompletenessComparisonRecordGroup[] {
    return this.props.recordGroups ?? [];
  }

  get visibleRecordGroups(): BgeCompletenessComparisonRecordGroup[] {
    return this.recordGroups.filter(
      (group) => this.recordsFor(group).length > 0
    );
  }

  get itemList(): BgeCompletenessComparisonItemList | undefined {
    return this.props.itemList;
  }

  get showCoordinates(): boolean {
    return (
      this.statusPassed === true &&
      !!this.props.coordinateColumns?.length &&
      this.props.coordinateColumns.every((column) =>
        this.hasDisplayValue(this.readValue(column.key))
      )
    );
  }

  get statusPassed(): boolean | null | undefined {
    return this.readBoolean(this.props.statusKey);
  }

  get statusMessage(): string | null | undefined {
    const message = this.readValue(this.props.messageKey);

    return typeof message === 'string' ? message : null;
  }

  get previewFileName(): string | null {
    const fileName = this.readValue(
      this.props.previewPath ?? 'matched_file_name'
    );
    return typeof fileName === 'string' && fileName.length > 0
      ? fileName
      : null;
  }

  get isPreviewOpen(): boolean {
    return this.isPreviewOpenFor(this.previewedFileName);
  }

  isPreviewOpenFor(fileName: string | undefined): boolean {
    return (
      !!fileName &&
      this.previewedFileName === fileName &&
      this.appFileViewerService.isVisible === true &&
      this.appFileViewerService.viewer?.filePath === this.previewObjectUrl
    );
  }

  readValue(path: string): unknown {
    return this.resolvePath(path, this.source);
  }

  displayValue(
    value: unknown,
    column?: BgeCompletenessComparisonColumn
  ): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'boolean' && column?.format === 'boolean') {
      return value ? column.trueLabel ?? 'Yes' : column.falseLabel ?? 'No';
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.displayValue(item, column)).join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    const display = String(value);
    return column?.format === 'identifier'
      ? this.humanizeIdentifier(display)
      : display;
  }

  recordsFor(group: BgeCompletenessComparisonRecordGroup): JsonRecord[] {
    const records = this.readValue(group.key);
    return Array.isArray(records)
      ? records.map((record) => this.asRecord(record))
      : [];
  }

  pagedRecordsFor(group: BgeCompletenessComparisonRecordGroup): JsonRecord[] {
    const records = this.recordsFor(group);
    const pageSize = this.pageSizeFor(group);
    const pageIndex = this.pageIndexFor(group);
    const start = pageIndex * pageSize;

    return records.slice(start, start + pageSize);
  }

  pageSizeFor(group: BgeCompletenessComparisonRecordGroup): number {
    const configuredPageSize = group.pageSize;
    return typeof configuredPageSize === 'number' && configuredPageSize > 0
      ? configuredPageSize
      : 10;
  }

  pageIndexFor(group: BgeCompletenessComparisonRecordGroup): number {
    const pageSize = this.pageSizeFor(group);
    const lastPageIndex = Math.max(
      Math.ceil(this.recordsFor(group).length / pageSize) - 1,
      0
    );

    return Math.min(this.recordPageIndexes.get(group.key) ?? 0, lastPageIndex);
  }

  onRecordPage(groupKey: string, event: PageEvent): void {
    this.recordPageIndexes.set(groupKey, event.pageIndex);
  }

  recordColumnId(columnIndex: number): string {
    return `record-column-${columnIndex}`;
  }

  recordColumnIds(group: BgeCompletenessComparisonRecordGroup): string[] {
    return [
      'record-title',
      ...(group.columns ?? []).map((_, index) => this.recordColumnId(index)),
    ];
  }

  recordTitleLabel(group: BgeCompletenessComparisonRecordGroup): string {
    return group.titleLabel ?? 'Match';
  }

  recordTitle(
    group: BgeCompletenessComparisonRecordGroup,
    record: JsonRecord
  ): string {
    return this.displayValue(this.valueFrom(record, group.titleKey));
  }

  recordLink(
    group: BgeCompletenessComparisonRecordGroup,
    record: JsonRecord
  ): string | null {
    const link = group.linkKey ? this.valueFrom(record, group.linkKey) : null;
    return typeof link === 'string' && link.length > 0 ? link : null;
  }

  itemsFor(list: BgeCompletenessComparisonItemList): JsonRecord[] {
    const items = this.readValue(list.key);
    return Array.isArray(items) ? items.map((item) => this.asRecord(item)) : [];
  }

  itemTitle(item: JsonRecord, list: BgeCompletenessComparisonItemList): string {
    return this.displayValue(this.valueFrom(item, list.titleKey));
  }

  itemStatus(
    item: JsonRecord,
    list: BgeCompletenessComparisonItemList
  ): boolean | null | undefined {
    const value = this.valueFrom(item, list.statusKey);
    return typeof value === 'boolean'
      ? value
      : value === null
      ? null
      : undefined;
  }

  itemMessage(
    item: JsonRecord,
    list: BgeCompletenessComparisonItemList
  ): string | null | undefined {
    const value = list.messageKey
      ? this.valueFrom(item, list.messageKey)
      : undefined;
    return typeof value === 'string' ? value : null;
  }

  itemDisplayMessage(
    item: JsonRecord,
    list: BgeCompletenessComparisonItemList
  ): string | null | undefined {
    const message = this.itemMessage(item, list);
    if (
      message &&
      message.trim().toLocaleLowerCase() ===
        this.itemTitle(item, list).trim().toLocaleLowerCase()
    ) {
      return null;
    }

    return message;
  }

  itemPreviewFileName(
    item: JsonRecord,
    list: BgeCompletenessComparisonItemList
  ): string | null {
    const value = this.valueFrom(item, list.previewPath ?? 'matched_file_name');
    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  togglePreview(fileName = this.previewFileName): void {
    if (!fileName || !this.applicationId) {
      return;
    }

    if (this.isPreviewOpenFor(fileName)) {
      this.appFileViewerService.hide();
      return;
    }

    if (this.previewObjectUrl && this.previewedFileName === fileName) {
      this.showPreview();
      return;
    }

    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = undefined;
    }

    this.previewedFileName = fileName;
    this.isPreviewLoading = true;
    this.appFileViewerService
      .getApplicationFilePreview(this.applicationId, fileName)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe({
        next: (file: Blob) => {
          this.previewObjectUrl = URL.createObjectURL(file);
          this.isPreviewLoading = false;
          this.showPreview();
        },
        error: () => {
          this.isPreviewLoading = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  private showPreview(): void {
    const previewObjectUrl = this.previewObjectUrl;
    if (!previewObjectUrl) {
      return;
    }

    this.appFileViewerService.hide();
    this.appFileViewerService.setFile(previewObjectUrl);
    this.appFileViewerService.show();
    this.changeDetectorRef.markForCheck();
  }

  private readBoolean(path: string): boolean | null | undefined {
    const value = this.readValue(path);
    if (typeof value === 'boolean') {
      return value;
    }

    return value === null ? null : undefined;
  }

  valueFrom(record: JsonRecord, path: string): unknown {
    return this.resolvePath(path, record);
  }

  private get rootModel(): unknown {
    let root = this.field as FormlyFieldConfig;
    while (root.parent) {
      root = root.parent;
    }

    return root.model;
  }

  private resolvePath(path: string, object: unknown): unknown {
    if (!path) {
      return object;
    }

    return path.split('.').reduce<unknown>((current, key) => {
      if (current === null || current === undefined) {
        return undefined;
      }

      if (typeof current !== 'object') {
        return undefined;
      }

      return (current as JsonRecord)[key];
    }, object);
  }

  private hasDisplayValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private humanizeIdentifier(value: string): string {
    return value
      .replace(/([a-z\d])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (character) => character.toLocaleUpperCase());
  }

  private asRecord(value: unknown): JsonRecord {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as JsonRecord)
      : {};
  }
}
